import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import classNames from "classnames";
import { LoyaltyApi } from "@jti/rest-api";
import { selectIsUserDataFetching, useSelector } from "@jti/store";
import { Button, DEPRECATED_Typography as Typography, Loading, LoadMore, useTranslations } from "@jti/ui";
import useLoggedUser from "../../helpers/hooks/useLoggedUser";
import { RedeemDialog } from "../RedeemDialog/RedeemDialog";
import ItemsList from "./ItemsList/ItemsList";
import styles from "./SimplifiedPrizes.module.scss";
const teaserFallbackData = { teaserTitle: "", teaserLinkUrl: "", teaserLinkText: "" };
export const SimplifiedPrizes = ({ ctaLabel = "Redeem now", pointsLabel = "points", costLabel = "Cost", availablePointsLabel = "Available Points", balanceAfterRedemptionLabel = "Balance after redemption", primaryCta = "Confirm", secondaryCta = "Cancel", dialogTitle = "Confirm your reward", dialogTitleConfirmation = "Congratulations", confirmationMessage = "{#redeemedPoints#} points redeemed for {#voucherName#}", confirmationMessagePlural = "{#redeemedPoints#} points redeemed for {#qty#} x {#voucherName#}", codeLabel = "Your code:", codeInfo = "Your code is stored in <a href='#'> points transaction history</a>.", bottomText = "By clicking you agree to our <a href='#'> terms and conditions</a>.", color = "white", noOffersText, emailHint, emailLabel, newOfferDays, newLabel = "New", oneDayLabel, daysLabel, unitLabel, isTeaser, teaserData = teaserFallbackData, pagination = 3, loadMoreButtonLabel = "Load more", errorMessage = "Something went wrong. Please try again later.", outOfStockLabel = "Out of stock", externalCodeButtonLabel = "Get your code", earnPointsCtaText, earnPointsCtaUrl, }) => {
    const [dialogData, setDialogData] = useState({});
    const [prizes, setPrizes] = useState([]);
    const [page, setPage] = useState(1);
    const [showLoadMoreButton, setShowLoadMoreButton] = useState(!isTeaser);
    const [hasError, setHasError] = useState(false);
    const [isPrizesLoaded, setIsPrizesLoaded] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const tier = useSelector((state) => state.loyaltyCore.tier);
    const { __ } = useTranslations();
    const { points: { balance }, } = useSelector((state) => state.loyaltyCore);
    const isUserDataFetching = useSelector(selectIsUserDataFetching);
    const [isLogged] = useLoggedUser();
    const { teaserTitle, teaserLinkUrl, teaserLinkText } = teaserData;
    const className = classNames(styles.simplifiedPrizes, {
        [styles.teaser]: isTeaser,
    });
    const resetDialogData = () => setDialogData({});
    const dialogLabels = {
        costLabel,
        availablePointsLabel,
        balanceAfterRedemptionLabel,
        primaryCta,
        secondaryCta,
        dialogTitle,
        dialogTitleConfirmation,
        bottomText,
        pointsLabel,
        confirmationMessage,
        confirmationMessagePlural,
        codeLabel,
        codeInfo,
        emailHint,
        emailLabel,
        unitLabel,
        externalCodeButtonLabel,
    };
    const handleGetPrizesRequest = useCallback(({ sort, orderBy }) => {
        return LoyaltyApi.init().getPrizes({
            page: page,
            perPage: pagination,
            sort: sort,
            orderBy: orderBy,
            is_virtual: 1,
        });
    }, [page, pagination]);
    const handleLoadMoreClick = useCallback(async () => {
        setIsLoadingMore(true);
        try {
            const response = await handleGetPrizesRequest({ sort: "asc", orderBy: "points" });
            const updatedPrizes = [...prizes, ...response.items];
            if (updatedPrizes.length >= response.total) {
                setShowLoadMoreButton(false);
            }
            setPrizes(updatedPrizes);
            setPage((prevPage) => prevPage + 1);
        }
        catch (error) {
            console.error(error);
            toast.error(__("mobile.error.something_went_wrong"));
        }
        finally {
            setIsLoadingMore(false);
        }
    }, [handleGetPrizesRequest, prizes, __]);
    const loadMoreButton = useMemo(() => {
        if (isTeaser || !showLoadMoreButton)
            return null;
        return isLoadingMore ? (_jsx(Loading, { dataTestId: "loadMoreLoading" })) : (_jsx(LoadMore, { label: loadMoreButtonLabel, "data-testid": "simplifiedPrizesLoadMore", onClick: handleLoadMoreClick }));
    }, [isTeaser, showLoadMoreButton, isLoadingMore, loadMoreButtonLabel, handleLoadMoreClick]);
    useEffect(() => {
        const fetchPrizes = async () => {
            const sortType = isTeaser ? "desc" : "asc";
            const orderByType = isTeaser ? "created_at" : "points";
            try {
                const response = await handleGetPrizesRequest({ sort: sortType, orderBy: orderByType });
                if (response.items.length >= response.total || response.total <= pagination) {
                    setShowLoadMoreButton(false);
                }
                setPrizes(response.items);
                setPage((prevPage) => prevPage + 1);
            }
            catch (error) {
                console.error(error);
                setHasError(true);
            }
            finally {
                setIsPrizesLoaded(true);
            }
        };
        fetchPrizes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (hasError)
        return (_jsx(Typography, { type: "h1", className: styles.error, color: "black", children: errorMessage }));
    return (_jsxs("div", { className: className, children: [isTeaser && (_jsx(Typography, { type: "text", className: styles.title, children: teaserTitle })), isPrizesLoaded && !isUserDataFetching ? (_jsxs("div", { className: styles.listWrapper, children: [_jsx(ItemsList, { items: prizes, pointsLabel: pointsLabel, ctaLabel: ctaLabel, color: color, noOffersText: noOffersText, points: balance, setDialogData: setDialogData, newBadgeData: { newOfferDays: newOfferDays, label: newLabel }, limitedOfferLabels: { daysLabel, oneDayLabel }, tier: tier.name, outOfStockLabel: outOfStockLabel, isLogged: isLogged, earnPointsCtaText: earnPointsCtaText, earnPointsCtaUrl: earnPointsCtaUrl }), _jsx("div", { className: styles.button, children: loadMoreButton }), isTeaser && (_jsx(Button, { variant: "redirect", color: "secondary", size: "large", className: styles.link, href: teaserLinkUrl, children: teaserLinkText }))] })) : (_jsx(Loading, { dataTestId: "prizesLoading", className: styles.loader })), _jsx(RedeemDialog, { pointsBalance: balance, prizeData: dialogData, onCloseDialog: resetDialogData, labels: dialogLabels })] }));
};
//# sourceMappingURL=SimplifiedPrizes.js.map